import { mergeSxProps, Pill, Text } from "@clipboard-health/ui-components";
import { Box, CardActionArea, Stack, type SxProps, type Theme } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useInView } from "react-intersection-observer";
import { generatePath, useHistory } from "react-router-dom";

import { ShiftCardWrapper } from "../../Shift/Card/Wrapper";
import { BookingCardContentWrapper } from "../../Shift/CardContentWrapper";
import { ShiftQualificationLabel } from "../../Shift/QualificationLabel";
import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_WORKER_PAY_SURVEY_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useGetWorkerPaySurveys } from "../api/useGetWorkerPaySurveys";

interface WorkerPaySurveyCardProps {
  surveyId: string;
  sx?: SxProps<Theme>;
}

export function WorkerPaySurveyCard(props: WorkerPaySurveyCardProps) {
  const { surveyId, sx } = props;
  const { data: surveysData } = useGetWorkerPaySurveys();

  const survey = surveysData?.data.find((s) => s.id === surveyId);
  const history = useHistory();

  const { ref } = useInView({
    threshold: 0.5,
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && survey) {
        logEvent(APP_V2_USER_EVENTS.WORKER_PAY_SURVEY_CARD_VIEWED, {
          survey_id: survey.id,
          title: survey.attributes.title,
        });
      }
    },
  });

  if (!survey) {
    return null;
  }

  const { title, subtitle, bonusInMinorUnits, qualification } = survey.attributes;
  const formattedBonus = formatCentsAsUsd(bonusInMinorUnits, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <Box ref={ref}>
      <ShiftCardWrapper
        variant="quaternary"
        sx={mergeSxProps(
          {
            display: "flex",
            background: (theme) => theme.palette.shift?.inAppAds.background,
            border: (theme) => `1px solid ${theme.palette.shift?.priority.border}`,
          },
          sx
        )}
        data-testid={`worker-pay-survey-card-${survey.id}`}
      >
        <CardActionArea
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.WORKER_PAY_SURVEY_CARD_CLICKED, {
              survey_id: survey.id,
              title,
              action: "view_survey",
            });

            const modalPath = generatePath(
              `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${SHIFT_DISCOVERY_WORKER_PAY_SURVEY_MODAL_PATH}`,
              { workerPaySurveyId: survey.id }
            );

            history.push(modalPath);
          }}
        >
          <BookingCardContentWrapper>
            <Stack spacing={4}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Pill
                  label="Survey"
                  size="small"
                  variant="filled"
                  color={(theme) => theme.palette.shift?.priority.text}
                  sx={{
                    width: "fit-content",
                    background: (theme) => theme.palette.shift?.priority.background,
                  }}
                />
                <Stack alignItems="flex-end" spacing={1}>
                  <Text semibold variant="body1">
                    {formattedBonus}
                  </Text>
                  <Text variant="body2" color={(theme) => theme.palette.text.tertiary}>
                    Earn a Bonus
                  </Text>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Text semibold variant="body1">
                  {title}
                </Text>
                <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                  {subtitle}
                </Text>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={3}>
                <ShiftQualificationLabel qualificationText={qualification} />
              </Stack>
            </Stack>
          </BookingCardContentWrapper>
        </CardActionArea>
      </ShiftCardWrapper>
    </Box>
  );
}
